import { useEffect, useState, type JSX } from "react";
import {
  BUILDING_INFO,
  buildingPrimaryStat,
  buildingSecondaryStat,
  buildingUpgradeCostAt,
  buildingUpgradeDurationMsAt,
  type BuildingFamily,
} from "@stahlfront/core";
import { fetchConstructionList, getMe, upgradeBuilding } from "../api.js";
import { useStore } from "../store.js";

/**
 * Phase 2.C — confirmation modal for an upgrade attempt.
 * Shows cost + duration + stat delta preview, then POSTs /buildings/upgrade
 * and refreshes wallet + construction list. Closes itself on success.
 */
export function UpgradeConfirmModal(): JSX.Element | null {
  const buildingId = useStore((s) => s.upgradeConfirmBuildingId);
  const setBuildingId = useStore((s) => s.setUpgradeConfirmBuildingId);
  const buildings = useStore((s) => s.buildings);
  const me = useStore((s) => s.me);
  const setMe = useStore((s) => s.setMe);
  const setConstructionJobs = useStore((s) => s.setConstructionJobs);

  const [pending, setPending] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  useEffect(() => {
    if (!buildingId) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !pending) setBuildingId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [buildingId, pending, setBuildingId]);

  if (!buildingId) return null;

  const building = buildings.find((b) => b.id === buildingId);
  if (!building) return null;

  const family = building.family as BuildingFamily;
  const meta = BUILDING_INFO[family];
  const fromLevel = building.level;
  const toLevel = fromLevel + 1;
  const cost = buildingUpgradeCostAt(family, fromLevel);
  const durationMs = buildingUpgradeDurationMsAt(family, fromLevel);
  const accent = `oklch(58% 0.16 ${meta.accentHue})`;
  const accentTitle = `oklch(36% 0.10 ${meta.accentHue})`;

  const primaryNow = buildingPrimaryStat(family, fromLevel);
  const primaryNext = buildingPrimaryStat(family, toLevel);
  const secondaryNow = buildingSecondaryStat(family, fromLevel);
  const secondaryNext = buildingSecondaryStat(family, toLevel);

  const treasuryNow = me?.wallet.stockpile.TREASURY ?? 0;
  const canAfford = treasuryNow >= cost;

  const close = (): void => {
    if (pending) return;
    setBuildingId(null);
    setErrorText(null);
  };

  const onConfirm = async (): Promise<void> => {
    if (pending || !canAfford || !me) return;
    setPending(true);
    setErrorText(null);
    try {
      const res = await upgradeBuilding({ baseBuildingId: building.id });
      setMe({ ...me, wallet: res.wallet });
      const list = await fetchConstructionList(me.base.id);
      setConstructionJobs(list.jobs);
      setBuildingId(null);
    } catch (err) {
      const m = String(err);
      if (m.includes("INSUFFICIENT_TREASURY")) setErrorText("Treasury too low for this upgrade.");
      else if (m.includes("ALREADY_UPGRADING")) setErrorText("Building already upgrading.");
      else if (m.includes("NO_CONSTRUCTION_SLOTS"))
        setErrorText("No construction slots free at this base.");
      else if (m.includes("LEVEL_CAPPED")) setErrorText("Already at max level for the MVP.");
      else setErrorText("Couldn't start upgrade. Try again.");
      // Wallet may have shifted (defensive refresh).
      const fresh = await getMe().catch(() => null);
      if (fresh) setMe(fresh);
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      className="upg-backdrop"
      onPointerDown={close}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upg-title"
    >
      <div
        className="upg-card"
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          ["--upg-accent" as string]: accent,
          ["--upg-accent-title" as string]: accentTitle,
        }}
      >
        <header className="upg-titlebar">
          <span id="upg-title" className="upg-title">
            Upgrade {meta.displayName}
          </span>
          <button type="button" className="upg-close" aria-label="Close upgrade dialog" onClick={close}>
            ×
          </button>
        </header>

        <section className="upg-rows" aria-label="Upgrade preview">
          <div className="upg-row">
            <span className="upg-row-label">Level</span>
            <span className="upg-row-value">
              Lv {fromLevel} <span className="upg-arrow">→</span> Lv {toLevel}
            </span>
          </div>
          <div className="upg-row">
            <span className="upg-row-label">Cost</span>
            <span className={`upg-row-value ${canAfford ? "" : "upg-row-value-bad"}`}>
              {fmt(cost)} treasury
            </span>
          </div>
          <div className="upg-row">
            <span className="upg-row-label">Duration</span>
            <span className="upg-row-value">{formatDuration(durationMs)}</span>
          </div>
          <div className="upg-row">
            <span className="upg-row-label">{meta.primaryStatLabel}</span>
            <span className="upg-row-value">
              {fmt(primaryNow)}
              {meta.primaryStatUnit ? ` ${meta.primaryStatUnit}` : ""}
              <span className="upg-arrow">→</span>
              {fmt(primaryNext)}
              {meta.primaryStatUnit ? ` ${meta.primaryStatUnit}` : ""}
              <span className="upg-delta">+{deltaPct(primaryNow, primaryNext)}%</span>
            </span>
          </div>
          <div className="upg-row">
            <span className="upg-row-label">{meta.secondaryStatLabel}</span>
            <span className="upg-row-value">
              {fmt(secondaryNow)}
              {meta.secondaryStatUnit ? ` ${meta.secondaryStatUnit}` : ""}
              <span className="upg-arrow">→</span>
              {fmt(secondaryNext)}
              {meta.secondaryStatUnit ? ` ${meta.secondaryStatUnit}` : ""}
              {secondaryNow > 0 ? (
                <span className="upg-delta">+{deltaPct(secondaryNow, secondaryNext)}%</span>
              ) : null}
            </span>
          </div>
        </section>

        {errorText ? (
          <p className="upg-error" role="alert">
            {errorText}
          </p>
        ) : null}

        <footer className="upg-footer">
          <button
            type="button"
            className="upg-btn upg-btn-secondary"
            onClick={close}
            disabled={pending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="upg-btn upg-btn-primary"
            onClick={() => void onConfirm()}
            disabled={pending || !canAfford}
            aria-busy={pending}
          >
            {pending ? (
              <>
                <span className="upg-spinner" aria-hidden="true" /> Starting…
              </>
            ) : canAfford ? (
              "Confirm"
            ) : (
              "Insufficient treasury"
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}

function fmt(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)} M`;
  if (value >= 10_000) return `${(value / 1000).toFixed(1)} k`;
  return new Intl.NumberFormat("en-US").format(value);
}

function deltaPct(a: number, b: number): number {
  if (a <= 0) return 0;
  return Math.round(((b - a) / a) * 100);
}

function formatDuration(ms: number): string {
  if (ms <= 0) return "0s";
  const sec = Math.ceil(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rsec = sec % 60;
  if (min < 60) return `${min}m ${rsec.toString().padStart(2, "0")}s`;
  const h = Math.floor(min / 60);
  const rmin = min % 60;
  return `${h}h ${rmin.toString().padStart(2, "0")}m`;
}
